import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Download, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { RichTextEditor } from '../comms/RichTextEditor';
import { DraftActions } from './DraftActions';
import { bulletinsAPI, errMsg } from '../../lib/regulatoryAPI';

const toText = (html) => { const el = document.createElement('div'); el.innerHTML = html || ''; return el.innerText.trim(); };

const DraftEditor = ({ d, meta, onChanged }) => {
  const [html, setHtml] = useState(d.content_html);
  const [title, setTitle] = useState(d.title);
  const locked = d.status === 'Approved' || !meta.can_write;
  const count = toText(html).length;
  const save = async () => { try { await bulletinsAPI.editDraft(d.id, { title, content_html: html }); toast.success('Draft saved'); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const copy = async () => { await navigator.clipboard.writeText(toText(html)); toast.success('Copied to clipboard'); };
  const exportFile = () => { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([`<h1>${title}</h1>\n${html}`], { type: 'text/html' })); a.download = `${title.slice(0, 40) || 'draft'}.html`; a.click(); };
  return (
    <div className="space-y-3 rounded-lg border bg-card p-4" data-testid={`draft-${d.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{d.type}</span>
        <span className="rounded-full border px-2 py-0.5 text-[11px]" data-testid={`draft-status-${d.id}`}>{d.status}</span>
        <span className="text-xs text-muted-foreground">From: {d.bulletin_title}</span>
        {d.type === 'LinkedIn post' && <span className={`ml-auto text-xs ${count > meta.linkedin_max ? 'font-semibold text-red-600' : 'text-muted-foreground'}`} data-testid={`draft-charcount-${d.id}`}>{count} / {meta.linkedin_max}</span>}
      </div>
      {d.reject_reason && d.status !== 'Approved' && <p className="rounded-md bg-red-50 p-2 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-300" data-testid={`draft-reject-reason-${d.id}`}>Rejected: {d.reject_reason}</p>}
      <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={locked} data-testid={`draft-title-${d.id}`} />
      <RichTextEditor initialHtml={d.content_html} onChange={setHtml} disabled={locked} testId={`draft-editor-${d.id}`} />
      <div className="flex flex-wrap gap-2">
        {!locked && <Button size="sm" onClick={save} data-testid={`draft-save-${d.id}`}>Save</Button>}
        <Button size="sm" variant="outline" disabled={!meta.ai_available} title={meta.ai_available ? '' : 'AI not configured'} data-testid={`draft-ai-${d.id}`}><Sparkles className="mr-1 h-3.5 w-3.5" />{meta.ai_available ? 'AI draft' : 'AI draft – AI not configured'}</Button>
        <Button size="sm" variant="ghost" onClick={copy} data-testid={`draft-copy-${d.id}`}><Copy className="mr-1 h-3.5 w-3.5" />Copy</Button>
        <Button size="sm" variant="ghost" onClick={exportFile} data-testid={`draft-export-${d.id}`}><Download className="mr-1 h-3.5 w-3.5" />Export</Button>
        <DraftActions d={d} meta={meta} onChanged={onChanged} />
      </div>
    </div>
  );
};

export const ContentHub = ({ meta }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ type: '', status: '' });
  const load = useCallback(() => bulletinsAPI.drafts(f).then((r) => setRows(r.items)).catch((e) => toast.error(errMsg(e))), [f]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4 pt-2" data-testid="content-hub">
      <div className="flex flex-wrap items-center gap-2">
        <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="content-filter-type">
          <option value="">All types</option>{meta.draft_types.map((t) => <option key={t}>{t}</option>)}</select>
        <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="content-filter-status">
          <option value="">All statuses</option>{meta.draft_statuses.map((t) => <option key={t}>{t}</option>)}</select>
        <p className="text-xs text-muted-foreground">Create drafts from a bulletin on the Regulatory Radar. Nothing is posted automatically – copy or export approved content.</p>
      </div>
      {rows.length ? rows.map((d) => <DraftEditor key={`${d.id}-${d.updated_at}`} d={d} meta={meta} onChanged={load} />)
        : <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="content-empty">No drafts yet.</p>}
    </div>
  );
};
